import React, { useEffect, useMemo, useRef, useState } from "react";
import CommonInputContainer from "./CommonInputContainer";
import { countries } from "country-data";

function CommonCountryInput({
  value, // string (alpha-2 code), e.g., "US"
  onChange, // (code: string) => void
  label,
  error,
  className,
  placeholder = "Select country",
  required,
  icon,
  dropdownPosition,
}) {
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(false); // input shows search text when true
  const [searchText, setSearchText] = useState("");

  // Prepare options: assigned countries with alpha2 + name
  const options = useMemo(() => {
    return countries.all
      .filter((c) => c.status === "assigned" && c.alpha2 && c.name)
      .map((c) => ({
        code: c.alpha2.toUpperCase(),
        name: c.name,
      }));
  }, []);

  const selected = useMemo(
    () => options.find((o) => o.code === (value || "").toUpperCase()) || null,
    [options, value],
  );

  const displayValue = searchActive ? searchText : selected?.name || "";

  // Filter by code or name (case-insensitive)
  const filtered = useMemo(() => {
    const q = (searchText || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
    );
  }, [options, searchText]);

  const handleToggle = () => setIsOpen((s) => !s);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchActive(false);
      setSearchText("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleFocus = () => {
    setIsOpen(true);
    setSearchActive(true);
    setSearchText(""); // start with empty for quick searching
  };

  const handleBlur = () => {
    // Let click selection happen first; blur will also trigger on outside click
    setTimeout(() => {
      setIsOpen(false);
      setSearchActive(false);
      setSearchText("");
    }, 0);
  };

  const handleOptionClick = (code) => {
    setIsOpen(false);
    setSearchActive(false);
    setSearchText("");
    if (onChange) onChange(code); // send only the code
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const query = searchText.trim();
      if (!query) {
        e.preventDefault();
        return;
      }

      const exactCode = options.find(
        (o) => o.code.toLowerCase() === query.toLowerCase(),
      );
      if (exactCode) {
        handleOptionClick(exactCode.code);
        e.preventDefault();
        return;
      }

      const exactName = filtered.find(
        (o) => o.name.toLowerCase() === query.toLowerCase(),
      );
      if (exactName) {
        handleOptionClick(exactName.code);
        e.preventDefault();
        return;
      }

      if (filtered.length === 1) {
        handleOptionClick(filtered[0].code);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchActive(false);
      setSearchText("");
      e.currentTarget.blur();
    }
  };

  // Optional flag from code
  const flagFromCode = (code) => {
    if (!code) return "🏳️";
    const up = code.toUpperCase();
    return String.fromCodePoint(...[...up].map((c) => 127397 + c.charCodeAt()));
  };

  return (
    <CommonInputContainer
      label={label}
      className={className}
      error={!!error && !value}
    >
      <div ref={dropdownRef} className="relative w-full">
        <div
          className={`w-full h-12 items-center px-4 border ${
            error && !value ? "border-red-600" : "border-[#DBDBDB]"
          } bg-white rounded-lg placeholder:text-[#A4B1C1] focus:outline-none flex items-center gap-2 cursor-text`}
          onMouseDown={(e) => {
            // Focus the inner input when container is clicked
            const input = e.currentTarget.querySelector("input");
            if (input) input.focus();
          }}
        >
          {icon ? <span className="mr-1">{icon}</span> : null}

          <input
            type="text"
            value={displayValue}
            placeholder={placeholder}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 h-full outline-none bg-transparent text-black placeholder:text-[#A4B1C1]"
          />

          <img
            src="/IconChevronDown.svg"
            alt=""
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
              if (!isOpen) {
                setSearchActive(true);
                setTimeout(() => {
                  const input = e.currentTarget
                    .closest("div")
                    ?.querySelector("input");
                  input?.focus();
                }, 0);
              }
            }}
            className={`w-3 object-contain transition-all cursor-pointer ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>

        {isOpen && (
          <ul
            className={
              "absolute w-full mt-2 bg-white border border-[#DBDBDB] rounded-lg shadow-md z-10 max-h-64 overflow-auto" +
              " " +
              dropdownPosition
            }
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-[#A4B1C1]">No data</li>
            ) : (
              filtered.map((opt) => (
                <li
                  key={opt.code}
                  onMouseDown={(e) => e.preventDefault()} // prevent input blur before click
                  onClick={() => handleOptionClick(opt.code)}
                  className={`px-4 py-2 hover:bg-[#F0F4F8] cursor-pointer whitespace-normal flex items-center gap-2 ${
                    selected?.code === opt.code ? "!bg-[#F0F4F8]" : ""
                  }`}
                  title={opt.name}
                >
                  <span className="w-5 text-center">
                    {flagFromCode(opt.code)}
                  </span>
                  <span className="flex-1">{opt.name}</span>
                  <span className="text-[#5b6b7c] text-xs">{opt.code}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </CommonInputContainer>
  );
}

export default CommonCountryInput;
